import type React from "react";
import { useEffect, useState } from "react";

function useCountUp(
  target: number,
  duration: number,
  delay: number,
  resetKey: number,
): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isFinite(target)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    setValue(0);
    let raf: number | undefined;
    let start: number | undefined;
    const timer = setTimeout(() => {
      const step = (ts: number): void => {
        if (start === undefined) start = ts;
        const progress = Math.min((ts - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(target * eased);
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timer);
      if (raf !== undefined) cancelAnimationFrame(raf);
    };
  }, [target, duration, delay, resetKey]);
  return value;
}

interface AnimatedNumberProps {
  value: number;
  format: (v: number) => string;
  delay?: number;
  className?: string;
  resetKey: number;
}

export function AnimatedNumber({
  value,
  format,
  delay = 0,
  className,
  resetKey,
}: AnimatedNumberProps): React.JSX.Element {
  const animated = useCountUp(value, 900, delay, resetKey);
  return <span className={className}>{format(animated)}</span>;
}
